import { useParams } from 'react-router';
import AssignedCorrectiveActionsDetail from '../../views/AssignedCorrectiveActionsDetail';
import { useQuery } from '@tanstack/react-query';
import Loader from '../../components/Loader';
import { useApiClients } from '../../client/api/useApiClient';
import {
  UserList,
  GetAllDepartmentResponse,
  GetAllUsersListResponse,
  GetCorrectiveActionAssignedUsingIdResponse,
  Department,
  Assignees,
} from '@EHSNavigator/backend';
import { JSX, useState } from 'react';
import AssignedCorrectiveActionsDetailStepOne from '../../components/AssignedCorrectiveActionsDetailStepOne';
import { getAssignedCorrectiveActionsDetailSchema } from './schema';
import { useFormik } from 'formik';
import AssignedCorrectiveActionsDetailStepTwo from '../../components/AssignedCorrectiveActionsDetailStepTwo';
import moment from 'moment';
import { toast } from 'react-toastify';
import ErrorScreen from '../../components/ErrorScreen';

export default function AssignedCorrectiveActionsDetailPage() {
  const [currentStep, setCurrentStep] = useState<number>(1);
  const { CorrectiveActionApi, DepartmentApi, UserApi } = useApiClients();
  const params = useParams();
  const id = params['assigned-corrective-actions-id'] || '';

  const { data, isLoading, refetch, error } =
    useQuery<GetCorrectiveActionAssignedUsingIdResponse>({
      queryKey: ['get-corrective-assigned-by-', id],
      queryFn: async () => {
        const response =
          await CorrectiveActionApi.correctiveActionControllerGetCorrectiveActionById(
            {
              correctiveActionId: id,
            },
          );
        return response?.data;
      },
    });
  const { data: allDepartments, isLoading: isDepartmentLoading } =
    useQuery<GetAllDepartmentResponse>({
      queryKey: ['departments'],
      queryFn: async () => {
        const response = await DepartmentApi.departmentControllerFindAllDepartment({
          disablePagination: true,
        });
        return response?.data;
      },
    });
  const { data: allUserList, isLoading: isUserListLoading } =
    useQuery<GetAllUsersListResponse>({
      queryKey: ['user-list'],
      queryFn: async () => {
        const response = await UserApi.userControllerGetUsersListForAssignee();
        return response?.data;
      },
    });

  const assignedCorrectiveActionDetailHandler = useFormik({
    enableReinitialize: true,
    initialValues: {
      date: data?.correctiveAction?.dueDate,
      time: data?.correctiveAction?.dueTime,
      department: data?.correctiveAction?.departmentId,
      assignee:
        data?.correctiveAction?.assignees?.map(
          (node: Assignees) => node?.userId,
        ) || [],
      leadAssignee:
        (data?.correctiveAction?.assignees || [])?.length > 1 &&
        data?.correctiveAction?.leadAssignee?.userId,
      actionPriority: data?.correctiveAction?.priority,
      actionTitle: data?.correctiveAction?.title,
      description: data?.correctiveAction?.description,
    },
    validationSchema: getAssignedCorrectiveActionsDetailSchema(currentStep),
    onSubmit: async (values) => {
      function arraysDifferSet<T extends string | number | boolean>(
        arr1: T[],
        arr2: T[],
      ): boolean {
        if (arr1.length !== arr2.length) return true;

        const set1 = new Set(arr1);
        return arr2.some((item) => !set1.has(item));
      }
      const checkValues = {
        date:
          moment(values?.date).format('DD/MM/YYYY') ===
          moment(data?.correctiveAction?.dueDate).format('DD/MM/YYYY')
            ? null
            : (moment(values?.date).format('DD/MM/YYYY') as any),
        time:
          moment(values?.time, 'HH:mm A').format('hh:mmA') ===
          moment(data?.correctiveAction?.dueTime, 'HH:mm A').format('hh:mmA')
            ? null
            : (moment(values?.time, 'HH:mm A').format('hh:mmA') as any),
        departmentId:
          values.department === data?.correctiveAction?.departmentId
            ? null
            : values.department,
        leadId:
          values.leadAssignee === data?.correctiveAction?.leadAssignee?.userId
            ? null
            : values.leadAssignee
              ? values.leadAssignee
              : null,
        priority:
          values.actionPriority === data?.correctiveAction?.priority
            ? null
            : values.actionPriority,
        title:
          values.actionTitle === data?.correctiveAction?.title
            ? null
            : values.actionTitle,
        description:
          values.description === data?.correctiveAction?.description
            ? null
            : values.description,
        assigneeIds: arraysDifferSet(
          values.assignee,
          data?.correctiveAction?.assignees?.map(
            (innerNode: Assignees) => innerNode?.user?.id,
          ) as any,
        )
          ? values.assignee
          : null,
      };
      const filteredCheckValues = Object.fromEntries(
        Object.entries(checkValues).filter(([, value]) => value !== null),
      );

      try {
        const res = await CorrectiveActionApi.correctiveActionControllerUpdate({
          incidentId: String(data?.correctiveAction?.incidentId),
          correctiveActionId: String(data?.correctiveAction?.id),
          updateCorrectiveActionDto: {
            ...filteredCheckValues,
          },
        });
        toast.success(res?.data?.message);
        handleNextStep(1);
        refetch();
      } catch (error: any) {
        const errorMessage = error?.response?.data?.message;
        if (Array.isArray(errorMessage)) {
          errorMessage.forEach((msg: string) => toast.error(msg));
        } else {
          toast.error(errorMessage || 'An unexpected error occurred.');
        }
      }
    },
  });

  const handleNextStep = async (steps: number) => {
    if (steps < currentStep) {
      setCurrentStep(steps);
      window.scrollTo(0, 0);
    } else {
      const errors = await assignedCorrectiveActionDetailHandler.validateForm();
      if (Object.keys(errors).length === 0) {
        setCurrentStep(steps);
        window.scrollTo(0, 0);
      } else {
        const touchedFields = Object.keys(errors).reduce((acc: any, key) => {
          acc[key] = true;
          return acc;
        }, {});
        assignedCorrectiveActionDetailHandler.setTouched(touchedFields);
      }
    }
  };

  const departments =
    allDepartments?.departments?.map((node: Department) => ({
      label: node?.name,
      value: node?.id,
    })) || [];
  const userList =
    allUserList?.users?.map((node: UserList) => ({
      label: node?.fullName,
      value: node?.id,
    })) || [];

  function areObjectsEqual(obj1: any, obj2: any) {
    const keys1 = Object.keys(obj1);
    const keys2 = Object.keys(obj2);

    if (keys1.length !== keys2.length) return false;
    return keys1.every((key) => obj1[key] === obj2[key]);
  }

  const isFormUnchanged = areObjectsEqual(
    {
      date: moment(assignedCorrectiveActionDetailHandler.values?.date).format(
        'DD/MM/YYYY',
      ),
      time: assignedCorrectiveActionDetailHandler.values?.time,
      department: assignedCorrectiveActionDetailHandler.values?.department,
      assignee: JSON.stringify(
        assignedCorrectiveActionDetailHandler.values?.assignee,
      ),
      leadAssignee: assignedCorrectiveActionDetailHandler.values?.leadAssignee,
      actionPriority:
        assignedCorrectiveActionDetailHandler.values?.actionPriority,
      actionTitle: assignedCorrectiveActionDetailHandler.values?.actionTitle,
      description: assignedCorrectiveActionDetailHandler.values?.description,
    },
    {
      date: moment(data?.correctiveAction?.dueDate).format('DD/MM/YYYY'),
      time: data?.correctiveAction?.dueTime || '',
      department: data?.correctiveAction?.departmentId,
      assignee: JSON.stringify(
        data?.correctiveAction?.assignees?.map(
          (node: Assignees) => node?.userId,
        ) || [],
      ),
      leadAssignee:
        (data?.correctiveAction?.assignees || [])?.length > 1 &&
        data?.correctiveAction?.leadAssignee?.userId,
      actionPriority: data?.correctiveAction?.priority,
      actionTitle: data?.correctiveAction?.title,
      description: data?.correctiveAction?.description,
    },
  );

  const steps: Record<number, JSX.Element> = {
    1: (
      <AssignedCorrectiveActionsDetail
        handleSteps={handleNextStep}
        refetch={refetch}
        baseUrl="/assigned-corrective-actions"
        data={
          data?.correctiveAction as GetCorrectiveActionAssignedUsingIdResponse['correctiveAction']
        }
      />
    ),
    2: (
      <AssignedCorrectiveActionsDetailStepOne
        handleSteps={handleNextStep}
        data={
          data?.correctiveAction as GetCorrectiveActionAssignedUsingIdResponse['correctiveAction']
        }
        formHandler={assignedCorrectiveActionDetailHandler}
        departments={departments}
        userList={userList}
        isUnMatchValues={isFormUnchanged}
      />
    ),
    3: (
      <AssignedCorrectiveActionsDetailStepTwo
        handleSteps={handleNextStep}
        data={
          data?.correctiveAction as GetCorrectiveActionAssignedUsingIdResponse['correctiveAction']
        }
        formHandler={assignedCorrectiveActionDetailHandler}
        departments={departments}
        userList={userList}
      />
    ),
  };
  if (isLoading || isDepartmentLoading || isUserListLoading) {
    return <Loader />;
  }

  if (error) {
    return <ErrorScreen />;
  }
  return (
    <div className="flex w-full flex-1 flex-col px-15 pt-14 pb-10 md:px-25 lg:pr-14 lg:pb-20 lg:pl-8">
      {steps[currentStep]}
    </div>
  );
}
